import {
  useAkaAdviceBaseAmountSelector,
  useAkaAdviceNetIncomeSelector,
} from "Store/akaAdvice/akaAdviceSelectors";
import {
  CoverageNeed,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  isAkaGroupLifeTimeSelected,
  isDisabilityInsuranceSelected,
} from "Store/akaAdvice/mappers/mapToCartProducts/mapToCartProducts";

import { VALUE_TYPE } from "Components/NumberValue";
import { Description } from "Features/CheckPrice/Products/Advice/adviceStyled";
import { getPriceFormatFromValue } from "Formatting/index";
import { getPayment } from "Store/akaAdvice/interfaces/getters";
import { FormikProps } from "formik";
import React from "react";
import { getMonthly } from "../AkaAdviceCharts/AkaAdviceChartDisability/akaAdviceChartDisabilityMonthlyConfig";
import { AkaAdvicePriceFormValues } from "../interfaces";

interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
}

export const AkaDisabilityAdviceSummary = ({
  formProps,
  need,
}: Props): React.ReactElement => {
  const netIncome = useAkaAdviceNetIncomeSelector();
  const baseAmount = useAkaAdviceBaseAmountSelector();

  const effectivePaymentRateAkaGroupLifeTime = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
  ).effectivePaymentRate;
  const effectivePaymentRateDisabilityFlex = getPayment(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  ).effectivePaymentRate;

  const { timeLimitedDisabilityInterestGLevel, disabilityInsuredSumSlider } =
    formProps.values;
  const navPayout = need.existingCoverages.NAV_PAYOUT;
  const employerPayout = need.existingCoverages.EMPLOYER_PAYOUT;

  const disabilityFlexAdviceValue = isDisabilityInsuranceSelected(
    formProps.values,
  )
    ? (disabilityInsuredSumSlider ?? 0) * effectivePaymentRateDisabilityFlex
    : 0;
  const adviceValueTimeLimited = isAkaGroupLifeTimeSelected(formProps.values)
    ? Math.trunc(
        Number(timeLimitedDisabilityInterestGLevel) *
          baseAmount *
          effectivePaymentRateAkaGroupLifeTime,
      )
    : 0;

  const incomeWithoutInsurance = navPayout + employerPayout;
  const incomeGapWithoutInsurance = netIncome - incomeWithoutInsurance;

  const incomeWithInsuranceFirstFourYears =
    incomeWithoutInsurance + disabilityFlexAdviceValue + adviceValueTimeLimited;
  const incomeGapWithInsuranceFirstFourYears =
    netIncome - incomeWithInsuranceFirstFourYears;

  const incomeWithInsuranceFromFourthYear =
    incomeWithoutInsurance + disabilityFlexAdviceValue;
  const incomeGapWithInsuranceFromFourthYear =
    netIncome - incomeWithInsuranceFromFourthYear;

  return (
    <Description fw={400} size="14px" className="AdviceChartDescription">
      {`Hvis du blir 100 % ufør blir månedslønnen din etter skatt ${
        incomeGapWithoutInsurance < 0 ? "økt" : "redusert"
      } med ${getPriceFormatFromValue(
        Math.trunc(getMonthly(Math.abs(incomeGapWithoutInsurance))),
        VALUE_TYPE.NOK,
      )}. Med valgt forsikring blir månedslønnen din de første fire årene ${
        incomeGapWithInsuranceFirstFourYears < 0 ? "økt" : "redusert"
      } med: ${getPriceFormatFromValue(
        Math.trunc(getMonthly(Math.abs(incomeGapWithInsuranceFirstFourYears))),
        VALUE_TYPE.NOK,
      )}. Fra år fire blir månedslønnen din ${
        incomeGapWithInsuranceFromFourthYear < 0 ? "økt" : "redusert"
      } med ${getPriceFormatFromValue(
        Math.trunc(getMonthly(Math.abs(incomeGapWithInsuranceFromFourthYear))),
        VALUE_TYPE.NOK,
      )}`}
    </Description>
  );
};
